import { useCallback, useRef } from "react";

import { useGraphQLClient } from "./GraphQLClientProvider";
import { FeapiError } from "../graphql/types";
import type {
  Variables,
  Extensions,
  GraphqlErrorResponse,
  FetcherArg,
} from "../graphql/types";
import { useHeadersFetcher } from "../headers";
import { useMountedState } from "../utils";

const createUnhandledError = (error: unknown): FeapiError => {
  return new FeapiError(
    error instanceof Error ? error.message : String(error),
    {
      errorCode: "REQUEST_ERROR",
    },
  );
};

const parseError = <TData>(error: {
  response: GraphqlErrorResponse<TData>;
}): GraphqlErrorResponse<TData> => ({
  errors: error?.response?.errors || [createUnhandledError(error)],
  data: error?.response?.data,
  status: error?.response?.status,
  headers: error?.response?.headers,
});

export const useGraphQLFetching = <
  TData,
  TVars extends Variables | undefined,
>(): {
  fetcher: (args: FetcherArg<TVars>, endpoint: string) => Promise<TData>;
  extensions: Extensions | null;
  fetchTime: number | null;
} => {
  const [extensions, setExtensions] = useMountedState<Extensions | null>(null);
  const time = useRef<number | null>(null);
  const headersFetcher = useHeadersFetcher();
  const graphQLClient = useGraphQLClient();
  const fetcher = useCallback(
    async ({ query, variables }, graphQLEndpoint) => {
      const headers = headersFetcher ? await headersFetcher() : {};
      const startTime = Date.now();
      return graphQLClient
        .request<TData, TVars>({
          query,
          variables,
          headers,
          graphQLEndpoint,
        })
        .then(({ data, extensions }) => {
          if (extensions) setExtensions(extensions);
          time.current = Date.now() - startTime;
          return data;
        })
        .catch((error) => {
          if (error?.response?.extensions)
            setExtensions(error?.response?.extensions);

          return Promise.reject(parseError(error));
        });
    },
    [graphQLClient, headersFetcher, setExtensions],
  );
  return {
    fetcher,
    extensions,
    fetchTime: time.current,
  };
};
